/**
 * The Zoho Expense preset (piece 31; decision 12720): its connection, its
 * routes and the preset view the Import page reads. It is the CRM preset's
 * twin on the Books client: the same Server-based client and data center, a
 * refresh token of its own, and two read scopes. 🔨 Nothing is asked for that
 * writes, and the pull posts nothing.
 *
 * The authority is Zoho Expense's own API v1 reference: OAuth, Scopes,
 * Expense Reports and Expenses (https://www.zoho.com/expense/api/v1/).
 */
import type { BooksSourceKind, ImportBlockValue, ImportRunSummary } from './importRun';
import { zohoBooksHosts, type ZohoBooksDataCentre } from './zohoBooksSettings';

/** 🔒 The two read scopes Connect with Zoho Expense asks for (decision 12720): the expense reports, and the expenses with their receipts. */
export const ZOHO_EXPENSE_SCOPES: readonly string[] = ['ZohoExpense.expensereport.READ', 'ZohoExpense.expense.READ'];

/** 🔒 The one Settings row the preset holds (decision 12720): the sealed refresh token Connect writes. */
export const ZOHO_EXPENSE_REFRESH_TOKEN_KEY = 'zohoExpense.refreshToken';
export const ZOHO_EXPENSE_SETTING_KEYS: readonly string[] = [ZOHO_EXPENSE_REFRESH_TOKEN_KEY];
export const ZOHO_EXPENSE_SEALED_KEYS: readonly string[] = [ZOHO_EXPENSE_REFRESH_TOKEN_KEY];

/** 🔒 The header every Zoho Expense call carries its organization in, the id the Books rows hold (decision 12720). */
export const ZOHO_EXPENSE_ORGANIZATION_HEADER = 'X-com-zoho-expense-organizationid';

/** The preset's routes, relative to the API root. */
export const ZOHO_EXPENSE_IMPORT_PATH = '/import/zoho-expense';
export const ZOHO_EXPENSE_CONNECT_PATH = `${ZOHO_EXPENSE_IMPORT_PATH}/connect`;
export const ZOHO_EXPENSE_CALLBACK_PATH = `${ZOHO_EXPENSE_IMPORT_PATH}/callback`;
export const ZOHO_EXPENSE_DISCONNECT_PATH = `${ZOHO_EXPENSE_IMPORT_PATH}/disconnect`;
export const ZOHO_EXPENSE_RUNS_PATH = `${ZOHO_EXPENSE_IMPORT_PATH}/runs`;

/** The query word the Import page reads on its return from Zoho's consent page. */
export const ZOHO_EXPENSE_OUTCOME_PARAM = 'zohoExpense';

/** The redirect address registered in Zoho's console beside the Books one: the application's address, then `/api/import/zoho-expense/callback`. */
export function zohoExpenseRedirectUri(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/api${ZOHO_EXPENSE_CALLBACK_PATH}`;
}

/** Zoho's authorization address for Zoho Expense's scopes, on the data center the Books rows name, with the same offline grant. */
export function zohoExpenseAuthorizationUrl(input: { dataCentre: ZohoBooksDataCentre; clientId: string; redirectUri: string; state: string }): string {
  const query = new URLSearchParams({
    scope: ZOHO_EXPENSE_SCOPES.join(','),
    client_id: input.clientId,
    response_type: 'code',
    access_type: 'offline',
    prompt: 'consent',
    redirect_uri: input.redirectUri,
    state: input.state,
  });
  return `${zohoBooksHosts(input.dataCentre).accounts}/oauth/v2/auth?${query.toString()}`;
}

/** 🔒 Zoho Expense's API base on the data center's `zohoapis` host, the Books host with `/expense/v1` for `/books/v3`. */
export function zohoExpenseApiBase(dataCentre: ZohoBooksDataCentre): string {
  return zohoBooksHosts(dataCentre).api.replace(/\/books\/v3$/, '/expense/v1');
}

/** The Import page's return after Connect: the import screen with the outcome, and a reason code where it was refused. */
export function zohoExpenseImportReturn(appBaseUrl: string, outcome: 'connected' | 'refused', reason: string | null = null): string {
  const query = new URLSearchParams({ [ZOHO_EXPENSE_OUTCOME_PARAM]: outcome });
  if (outcome === 'refused' && reason && /^[A-Za-z]{1,30}$/.test(reason)) query.set('reason', reason);
  return `${appBaseUrl.replace(/\/+$/, '')}/admin/import?${query.toString()}`;
}

/** 🔒 The Zoho Expense preset as the Import page reads it (decision 12720). */
export interface ZohoExpensePresetView {
  source: BooksSourceKind;
  /** Whether Zoho Expense's own refresh token is held. */
  connected: boolean;
  /** Whether the Books client's id, secret and data center are held, which Connect with Zoho Expense needs. */
  clientReady: boolean;
  /** The redirect address to register in Zoho's console beside the Books one. */
  redirectUri: string;
  /** 🔒 The day the ledger opens, from which a read reads; null until a documents range is written. */
  cutoff: string | null;
  block: { block: ImportBlockValue; lastRun: ImportRunSummary | null; dependencyMet: boolean; cleanup: { runId: string; entries: number } | null };
  running: ImportRunSummary | null;
  staged: ImportRunSummary[];
}
